// @ts-strict-ignore
import clamp from 'lodash/clamp';
import isNumber from 'lodash/isNumber';
import round from 'lodash/round';

import {BookmarkYAxisOptions} from 'common/report/insights/util/types';
import {formatPercent} from 'common/util';

import {
  BAR_GAP_PX,
  DEFAULT_MINIMUM_STEP_WIDTH,
  MINIMUM_BAR_WIDTH,
  STEP_GAP_PX,
  STEP_RIGHT_PADDING_PX,
} from './constants';

export enum ConversionStepBarType {
  Normal = `normal`,
  Comparison = `comparison`,
}

export interface ConversionStepBar<ExtrasT = any> {
  displayName?: string;
  /* conversion rate from the previous step in its respective funnel between 0 - 1 */
  conversionRate: number;
  /* number of conversions in this step */
  value: number;
  /* color used to override default color scheme */
  color?: string;
  type?: ConversionStepBarType;

  /** can store arbitrary data associated with the bar, will be passed through in event handlers & callbacks */
  extras?: ExtrasT;
}

// placeholder steps don't contain any data, they visually fill space so that
export interface PlaceholderStep {
  placeholder: true;
}

export interface NormalConversionStep<StepExtrasT = any, BarExtrasT = any> {
  placeholder?: false;
  bars: Array<ConversionStepBar<BarExtrasT>>;
  displayName: string;
  tooltipName?: string; // more descriptive name only shown in a tooltip on hover

  /** can store arbitrary data associated with the conversion step, will be passed through in event handlers & callbacks */
  extras?: StepExtrasT;

  display?: any;
}

export type ConversionStep<StepExtrasT = any, BarExtrasT = any> =
  | PlaceholderStep
  | NormalConversionStep<StepExtrasT, BarExtrasT>;

// layout type should not be used outside the component
export interface ConversionStepBarLayout {
  /**
   * "conversionRate" is relative to the previous step. We want to store the
   * absolute conversion rate for use in computing the height. This is
   * specifically needed when we have an adjusted min and/or max. E.g. you have
   * a view with min of 0.5 and a max of 1.0, and the conversion rates are
   * [1.0, 0.75]. The heights should be [1.0, 0.5] respectively. If we computed
   * relative to the previous height we would get [1.0, 0.75] which is incorrect.
   */
  absoluteConversionRate: number;
  dropoffHeightPercent: number; // in range [0, 1]
  heightPercent: number; // in range [0, 1]
}

// layout type should not be used outside the component
export interface ConversionStepLayout {
  barLayouts: ConversionStepBarLayout[];
}

// throws errors if validation fails
export function validateConversionSteps(conversionSteps: ConversionStep[]): void {
  if (!Array.isArray(conversionSteps)) {
    throw new Error(`conversionSteps must be an array`);
  }

  const normalSteps = conversionSteps.filter((step) => !step.placeholder) as NormalConversionStep[];
  if (normalSteps.length > 0) {
    const barsPerStep = normalSteps[0].bars.length;
    if (normalSteps.some((step) => step.bars.length !== barsPerStep)) {
      throw new Error(`conversionSteps must have the same number of bars`);
    }
  }
}

// It's assumed that all steps have the same number of bars, this is enforced in validateConversionSteps()
export function getNumBarsPerStep(conversionSteps: ConversionStep[]): number {
  const normalSteps = conversionSteps.filter((step) => !step.placeholder) as NormalConversionStep[];
  if (normalSteps.length > 0) {
    return normalSteps[0].bars.length;
  }
  return 0;
}

export function getStepGap(conversionSteps: ConversionStep[]): number {
  // px
  // When each step only has one bar, we can reduce the gap between steps to reduce wasted whitespace.
  const barsPerStep = getNumBarsPerStep(conversionSteps);
  if (barsPerStep <= 1) {
    return BAR_GAP_PX;
  }

  return STEP_GAP_PX;
}

function getStepValues(steps: ConversionStep[]): number[] {
  return steps.flatMap((step) => {
    if (step.placeholder !== true) {
      // `!== true` required because strict null checks are not enabled in tsconfig
      return step.bars.map((barData) => barData.value);
    }
    return [];
  });
}

export function getMinValue(
  steps: ConversionStep[],
  yAxisOptions?: BookmarkYAxisOptions,
  percentBased = false,
): number {
  const stepValues = getStepValues(steps);
  const minStepValue = stepValues.length > 0 ? Math.min(...stepValues) : 0;
  // If the min step value is negative, we'll use it as the absolute min, otherwise we'll use 0
  let absoluteMin = minStepValue < 0 ? minStepValue : 0;
  // Percent based charts should always start at 0
  if (percentBased) {
    absoluteMin = 0;
  }
  if (isNumber(yAxisOptions?.min)) {
    // Soft min means we use the provided min from yAxisOptions unless there is
    // a value lower than it. Then we use that.
    if (yAxisOptions.useSoftMinMax) {
      return Math.min(absoluteMin, yAxisOptions.min);
    }
    return yAxisOptions.min;
  }
  return absoluteMin;
}

export function getMaxValue(steps: ConversionStep[], yAxisOptions?: BookmarkYAxisOptions): number {
  const stepValues = getStepValues(steps);
  const absoluteMax = stepValues.length > 0 ? Math.max(...stepValues) : 0;
  if (isNumber(yAxisOptions?.max)) {
    // Soft max means we use the provided max from yAxisOptions unless there is
    // a value higher than it. Then we use that.
    if (yAxisOptions.useSoftMinMax) {
      return Math.max(absoluteMax, yAxisOptions.max);
    }
    return yAxisOptions.max;
  }
  return absoluteMax;
}

function getFirstStepLayout(
  firstNormalStep: NormalConversionStep,
  max: number,
  valueBasedHeights: boolean,
  yAxisOptions?: BookmarkYAxisOptions,
): ConversionStepLayout {
  const min = getMinValue([firstNormalStep], yAxisOptions, !valueBasedHeights);
  // the first normal step's bars are always 100% unless there are zero conversions or are using valueBasedHeights
  const startsOnZero = getMaxValue([firstNormalStep]) === 0;
  const initialHeightPercent = startsOnZero ? 0 : 1;
  const barLayouts = firstNormalStep.bars.map((step) => {
    let heightPercent = initialHeightPercent;
    // Factor in potential custom min/max values and compute height relative
    // to them.
    if (valueBasedHeights && initialHeightPercent !== 0) {
      heightPercent = (step.value - min) / (max - min);
    }
    // Height percent should never exceed 100%
    heightPercent = Math.min(heightPercent, 1);
    return {
      absoluteConversionRate: heightPercent,
      heightPercent,
      dropoffHeightPercent: 0, // Drop off only occurs after the first step
    };
  });
  return {barLayouts};
}

export function getConversionStepLayouts(
  steps: ConversionStep[],
  valueBasedHeights = false,
  yAxisOptions?: BookmarkYAxisOptions,
): ConversionStepLayout[] {
  const stepLayouts: ConversionStepLayout[] = [];

  let previousNormalStepLayout: null | ConversionStepLayout = null; // layout for a previous normal step, ignoring layouts for previous placeholder steps
  const max = getMaxValue(steps, yAxisOptions);
  const min = getMinValue(steps, yAxisOptions, !valueBasedHeights);
  // For percents we'll accept values in the interval [0, 1]
  const percentMax = Math.min(max, 1);
  for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
    const step = steps[stepIndex];

    if (step.placeholder !== true) {
      // `!== true` required because strict null checks are not enabled in tsconfig
      if (previousNormalStepLayout) {
        const barLayouts = step.bars.map((bar, barIndex) => {
          const previousEntry = previousNormalStepLayout.barLayouts[barIndex];
          const absoluteConversionRate = previousEntry.absoluteConversionRate * bar.conversionRate;
          // Factor in potential custom min/max values and compute height relative
          // to them.
          let heightPercent = valueBasedHeights
            ? (bar.value - min) / (max - min)
            : (absoluteConversionRate - min) / (percentMax - min);
          // Bound heights to [0,100]%
          heightPercent = clamp(heightPercent, 0, 1);
          const dropoffHeightPercent = previousEntry.heightPercent - heightPercent;
          return {absoluteConversionRate, dropoffHeightPercent, heightPercent};
        });
        previousNormalStepLayout = {barLayouts};
      } else {
        previousNormalStepLayout = getFirstStepLayout(step, max, valueBasedHeights, yAxisOptions);
      }
      stepLayouts.push(previousNormalStepLayout);
    } else {
      stepLayouts.push({barLayouts: []});
    }
  }

  return stepLayouts;
}

export function getPercentLabel(percent: number /* in range [0, 1] */, precision?: number): string {
  const percentagePoints = percent * 100;
  if (percentagePoints >= 99.5) {
    return `100%`;
  }

  return formatPercent(percent, {precision, exactPrecision: precision !== undefined});
}

export function getStepWidth(
  conversionSteps: ConversionStep[],
  scrollContainer: HTMLElement,
  minStepWidth = DEFAULT_MINIMUM_STEP_WIDTH,
  maxStepWidth?: number,
): number {
  if (!scrollContainer) {
    return 0;
  }
  const barsPerStep = getNumBarsPerStep(conversionSteps);
  const minimumStepWidthForBars =
    barsPerStep > 1
      ? // Math.max adds a little step width when only 2 segments are selected
        MINIMUM_BAR_WIDTH * Math.max(barsPerStep, 3) +
        (getStepGap(conversionSteps) + STEP_RIGHT_PADDING_PX + (barsPerStep - 1) * BAR_GAP_PX)
      : MINIMUM_BAR_WIDTH + getStepGap(conversionSteps) + STEP_RIGHT_PADDING_PX;
  const minimumStepWidth = Math.max(minStepWidth, minimumStepWidthForBars);

  // if possible, return a step width that fills the whole scrollContainer width (or as much as max-step-width allows)
  if (scrollContainer && conversionSteps.length > 0) {
    const visibleWidth = scrollContainer.offsetWidth;
    if (minimumStepWidth * conversionSteps.length < visibleWidth) {
      const fillingStepWidth = visibleWidth / conversionSteps.length;
      return maxStepWidth ? Math.min(maxStepWidth, fillingStepWidth) : fillingStepWidth;
    }
  }
  return minimumStepWidth;
}

export const getCssHeightPercentLog = (
  heightPercent: number,
  minThreshold?: number,
  maxThreshold?: number,
  firstTick = 0.01,
  lastTick = 100,
) => {
  if (heightPercent === 0) {
    return 0;
  }
  if (minThreshold && heightPercent < minThreshold) {
    return 0;
  }
  if (maxThreshold && heightPercent > maxThreshold) {
    return 100;
  }
  const maxlog = Math.log10(lastTick);
  const minLog = Math.log10(firstTick);
  const heightLog = Math.log10(heightPercent * 100);
  if (heightLog > minLog) {
    // Round to two decimal places
    return round(((heightLog + Math.abs(minLog)) / (maxlog + Math.abs(minLog))) * 100, 2);
  }

  // Set zero log to 0.001 because log is asymptotic
  const zeroLog = Math.log10(0.001);
  return heightLog < zeroLog ? 0 : ((zeroLog - heightLog) / zeroLog) * 25;
};
